import React, { useEffect, useMemo, useState } from "react";
import Sidebar from "../components/Sidebar";
import Card from "../components/Card";
import { goldAPI, userAPI } from "../services/api";
import { tradeHistoryAPI } from "../services/tradeHistoryAPI";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  Legend,
  BarChart,
  Bar,
} from "recharts";

const TIME_RANGE_DAYS = {
  "7D": 7,
  "1M": 30,
  "3M": 90,
  "1Y": 365,
  "All Time": null,
};

const formatCurrency = (value) =>
  `$${(Number(value) || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatPercent = (value) => `${(Number(value) || 0).toFixed(1)}%`;

const getChangePercent = (currentValue, previousValue) => {
  const current = Number(currentValue) || 0;
  const previous = Number(previousValue) || 0;

  if (!previous) {
    if (!current) return 0;
    return 100;
  }

  return ((current - previous) / Math.abs(previous)) * 100;
};

const isDateInRange = (dateString, start, end) => {
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return false;
  if (start && date < start) return false;
  if (end && date > end) return false;
  return true;
};

const sumBy = (items, key) =>
  items.reduce((sum, item) => sum + (Number(item[key]) || 0), 0);

const getArbitrageNetProfit = (trade) => {
  if (typeof trade?.realizedNetProfit === "number") {
    return Number(trade.realizedNetProfit);
  }
  return Number(trade?.maxProfit || 0) - Number(trade?.estimatedFees || 0);
};

const TradingPerformance = () => {
  const [activeTimeRange, setActiveTimeRange] = useState("1M");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [orders, setOrders] = useState([]);
  const [pnlRecords, setPnlRecords] = useState([]);
  const [arbitrageTrades, setArbitrageTrades] = useState([]);
  const [incomeBySource, setIncomeBySource] = useState([]);

  const timeRanges = ["7D", "1M", "3M", "1Y", "All Time"];

  useEffect(() => {
    const loadPerformanceData = async () => {
      try {
        setLoading(true);
        setError("");

        const [ordersRes, pnlRes, incomeRes, arbitrageRes] = await Promise.all([
          goldAPI.getOrders({ limit: 1000 }),
          goldAPI.getPnL(),
          userAPI.getIncomeSummary(),
          tradeHistoryAPI.getHistory({ page: 1, limit: 1000 }),
        ]);

        setOrders(Array.isArray(ordersRes?.data) ? ordersRes.data : []);
        setPnlRecords(
          Array.isArray(pnlRes?.data?.records) ? pnlRes.data.records : [],
        );
        setArbitrageTrades(Array.isArray(arbitrageRes?.trades) ? arbitrageRes.trades : []);
        setIncomeBySource(incomeRes?.summary?.bySource || []);
      } catch (err) {
        setError(err?.error || "Failed to load trading performance data");
      } finally {
        setLoading(false);
      }
    };

    loadPerformanceData();
  }, []);

  const activeWindow = useMemo(() => {
    const end = new Date();
    const days = TIME_RANGE_DAYS[activeTimeRange];

    if (!days) {
      return { start: null, end, previousStart: null, previousEnd: null };
    }

    const start = new Date(end);
    start.setHours(0, 0, 0, 0);
    start.setDate(start.getDate() - (days - 1));

    const previousEnd = new Date(start.getTime() - 1);
    const previousStart = new Date(previousEnd);
    previousStart.setHours(0, 0, 0, 0);
    previousStart.setDate(previousStart.getDate() - (days - 1));

    return { start, end, previousStart, previousEnd };
  }, [activeTimeRange]);

  const currentOrders = useMemo(
    () =>
      orders.filter((order) =>
        isDateInRange(order.createdAt, activeWindow.start, activeWindow.end),
      ),
    [orders, activeWindow],
  );

  const currentArbitrageTrades = useMemo(
    () =>
      arbitrageTrades.filter((trade) =>
        isDateInRange(trade.createdAt, activeWindow.start, activeWindow.end),
      ),
    [activeWindow, arbitrageTrades],
  );

  const previousArbitrageTrades = useMemo(
    () =>
      arbitrageTrades.filter((trade) =>
        isDateInRange(
          trade.createdAt,
          activeWindow.previousStart,
          activeWindow.previousEnd,
        ),
      ),
    [activeWindow, arbitrageTrades],
  );

  const previousOrders = useMemo(
    () =>
      orders.filter((order) =>
        isDateInRange(
          order.createdAt,
          activeWindow.previousStart,
          activeWindow.previousEnd,
        ),
      ),
    [orders, activeWindow],
  );

  const currentPnL = useMemo(
    () =>
      pnlRecords.filter((record) =>
        isDateInRange(
          record.realizationDate,
          activeWindow.start,
          activeWindow.end,
        ),
      ),
    [pnlRecords, activeWindow],
  );

  const currentArbitragePnL = useMemo(
    () =>
      arbitrageTrades
        .filter(
          (trade) =>
            trade.status === "completed" &&
            isDateInRange(
              trade.completedAt || trade.updatedAt || trade.createdAt,
              activeWindow.start,
              activeWindow.end,
            ),
        )
        .map((trade) => ({
          realizationDate: trade.completedAt || trade.updatedAt || trade.createdAt,
          netPnL: getArbitrageNetProfit(trade),
        })),
    [activeWindow, arbitrageTrades],
  );

  const previousPnL = useMemo(
    () =>
      pnlRecords.filter((record) =>
        isDateInRange(
          record.realizationDate,
          activeWindow.previousStart,
          activeWindow.previousEnd,
        ),
      ),
    [pnlRecords, activeWindow],
  );

  const previousArbitragePnL = useMemo(
    () =>
      arbitrageTrades
        .filter(
          (trade) =>
            trade.status === "completed" &&
            isDateInRange(
              trade.completedAt || trade.updatedAt || trade.createdAt,
              activeWindow.previousStart,
              activeWindow.previousEnd,
            ),
        )
        .map((trade) => ({
          realizationDate: trade.completedAt || trade.updatedAt || trade.createdAt,
          netPnL: getArbitrageNetProfit(trade),
        })),
    [activeWindow, arbitrageTrades],
  );

  const metricCards = useMemo(() => {
    const currentTotalPnL =
      sumBy(currentPnL, "netPnL") + sumBy(currentArbitragePnL, "netPnL");
    const previousTotalPnL =
      sumBy(previousPnL, "netPnL") + sumBy(previousArbitragePnL, "netPnL");

    const currentWins =
      currentPnL.filter((item) => Number(item.netPnL) > 0).length +
      currentArbitragePnL.filter((item) => Number(item.netPnL) > 0).length;
    const previousWins =
      previousPnL.filter((item) => Number(item.netPnL) > 0).length +
      previousArbitragePnL.filter((item) => Number(item.netPnL) > 0).length;

    const currentTotalClosedTrades = currentPnL.length + currentArbitragePnL.length;
    const previousTotalClosedTrades = previousPnL.length + previousArbitragePnL.length;
    const currentWinRate = currentTotalClosedTrades
      ? (currentWins / currentTotalClosedTrades) * 100
      : 0;
    const previousWinRate = previousTotalClosedTrades
      ? (previousWins / previousTotalClosedTrades) * 100
      : 0;

    const currentTradeCount = currentOrders.length + currentArbitrageTrades.length;
    const previousTradeCount = previousOrders.length + previousArbitrageTrades.length;
    const currentTradeValue =
      sumBy(currentOrders, "totalAmount") + sumBy(currentArbitrageTrades, "investment");
    const previousTradeValue =
      sumBy(previousOrders, "totalAmount") + sumBy(previousArbitrageTrades, "investment");
    const currentAvgTrade = currentTradeCount
      ? currentTradeValue / currentTradeCount
      : 0;
    const previousAvgTrade = previousTradeCount
      ? previousTradeValue / previousTradeCount
      : 0;

    const pnlChange = getChangePercent(currentTotalPnL, previousTotalPnL);
    const winRateChange = getChangePercent(currentWinRate, previousWinRate);
    const avgTradeChange = getChangePercent(currentAvgTrade, previousAvgTrade);
    const tradeCountChange = getChangePercent(currentTradeCount, previousTradeCount);

    return [
      {
        title: "Total Profit / Loss",
        value: formatCurrency(currentTotalPnL),
        change: `${pnlChange >= 0 ? "+" : ""}${pnlChange.toFixed(1)}%`,
        changeType: pnlChange >= 0 ? "positive" : "negative",
      },
      {
        title: "Win Rate",
        value: formatPercent(currentWinRate),
        change: `${winRateChange >= 0 ? "+" : ""}${winRateChange.toFixed(1)}%`,
        changeType: winRateChange >= 0 ? "positive" : "negative",
      },
      {
        title: "Average Trade Value",
        value: formatCurrency(currentAvgTrade),
        change: `${avgTradeChange >= 0 ? "+" : ""}${avgTradeChange.toFixed(1)}%`,
        changeType: avgTradeChange >= 0 ? "positive" : "negative",
      },
      {
        title: "Total Trades Executed",
        value: currentTradeCount.toLocaleString(),
        change: `${tradeCountChange >= 0 ? "+" : ""}${tradeCountChange.toFixed(1)}%`,
        changeType: tradeCountChange >= 0 ? "positive" : "negative",
      },
    ];
  }, [
    currentArbitragePnL,
    currentArbitrageTrades,
    currentOrders,
    currentPnL,
    previousArbitragePnL,
    previousArbitrageTrades,
    previousOrders,
    previousPnL,
  ]);

  const portfolioData = useMemo(() => {
    const sortedRecords = [...currentPnL, ...currentArbitragePnL].sort(
      (a, b) => new Date(a.realizationDate) - new Date(b.realizationDate),
    );

    let cumulative = 0;
    return sortedRecords.map((record) => {
      cumulative += Number(record.netPnL) || 0;
      return {
        period: new Date(record.realizationDate).toLocaleDateString(undefined, {
          month: "short",
          day: "numeric",
        }),
        value: Number(cumulative.toFixed(2)),
      };
    });
  }, [currentArbitragePnL, currentPnL]);

  const volumeData = useMemo(() => {
    const bucketMap = new Map();
    currentOrders.forEach((order) => {
      const date = new Date(order.createdAt);
      if (Number.isNaN(date.getTime())) return;
      const key = date.toISOString().slice(0, 10);
      const label = date.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
      });
      const existing = bucketMap.get(key) || { period: label, volume: 0 };
      existing.volume += Number(order.totalAmount) || 0;
      bucketMap.set(key, existing);
    });
    currentArbitrageTrades.forEach((trade) => {
      const date = new Date(trade.createdAt);
      if (Number.isNaN(date.getTime())) return;
      const key = date.toISOString().slice(0, 10);
      const label = date.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
      });
      const existing = bucketMap.get(key) || { period: label, volume: 0 };
      existing.volume += Number(trade.investment) || 0;
      bucketMap.set(key, existing);
    });

    return [...bucketMap.entries()]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([, value]) => ({ ...value, volume: Number(value.volume.toFixed(2)) }));
  }, [currentArbitrageTrades, currentOrders]);

  const incomeDistribution = useMemo(() => {
    const total = incomeBySource.reduce(
      (sum, source) => sum + (Number(source.totalNet) || 0),
      0,
    );
    if (!total) return [];

    const colors = ["#22c55e", "#16a34a", "#8B5A00", "#6B4500", "#10B981", "#3B82F6"];
    return incomeBySource.map((source, index) => ({
      name: String(source.source || "Unknown")
        .replaceAll("_", " ")
        .toLowerCase()
        .replace(/\b\w/g, (char) => char.toUpperCase()),
      value: Number((((Number(source.totalNet) || 0) / total) * 100).toFixed(1)),
      color: colors[index % colors.length],
    }));
  }, [incomeBySource]);

  const outcomeBreakdown = useMemo(() => {
    const combinedPnL = [...currentPnL, ...currentArbitragePnL];
    const profitable = combinedPnL.filter((item) => Number(item.netPnL) > 0).length;
    const loss = combinedPnL.filter((item) => Number(item.netPnL) < 0).length;
    const breakeven = combinedPnL.filter((item) => Number(item.netPnL) === 0).length;
    const total = combinedPnL.length || 1;

    return [
      { name: "Profitable", value: Number(((profitable / total) * 100).toFixed(1)) },
      { name: "Loss", value: Number(((loss / total) * 100).toFixed(1)) },
      { name: "Breakeven", value: Number(((breakeven / total) * 100).toFixed(1)) },
    ];
  }, [currentArbitragePnL, currentPnL]);

  return (
    <div className="flex min-h-screen bg-[var(--bg-main)] overflow-x-hidden">
      <Sidebar />
      <div className="flex-1 min-w-0 p-4 md:p-8 lg:ml-64 overflow-x-hidden">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold mb-2 bg-gradient-to-r from-[#22c55e] to-[#16a34a] bg-clip-text text-transparent">
            Trading Performance Details
          </h1>
          <p className="text-[var(--text-primary)] text-sm md:text-base">
            Comprehensive analytics and performance metrics
          </p>
        </div>

        {loading ? <p className="text-[var(--text-primary)] mb-4">Loading real performance data...</p> : null}
        {error ? <p className="text-red-400 mb-4">{error}</p> : null}

        {/* Time Range Filters */}
        <div className="mb-8 overflow-x-auto">
          <div className="flex w-max min-w-full gap-3 pb-2 pr-1">
            {timeRanges.map((range) => (
              <button
                key={range}
                onClick={() => setActiveTimeRange(range)}
                className={`px-6 py-3 rounded-xl font-medium whitespace-nowrap transition-all ${
                  activeTimeRange === range
                    ? "bg-gradient-to-r from-[#22c55e] to-[#16a34a] text-white"
                    : "bg-transparent border-2 border-primary/30 text-[var(--text-primary)] hover:border-primary hover:bg-[#22c55e]/5"
                }`}
              >
                {range}
              </button>
            ))}
          </div>
        </div>

        {/* Metric Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {metricCards.map((metric, index) => (
            <Card key={index}>
              <div className="mb-4">
                <p className="text-sm text-[var(--text-primary)] opacity-70 mb-2">
                  {metric.title}
                </p>
                <p className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-[#22c55e] to-[#16a34a] bg-clip-text text-transparent">
                  {metric.value}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <span
                  className={`text-sm font-semibold ${
                    metric.changeType === "positive"
                      ? "text-green-400"
                      : "text-red-400"
                  }`}
                >
                  {metric.change}
                </span>
                <span className="text-xs text-[var(--text-primary)] opacity-60">
                  vs previous range
                </span>
              </div>
            </Card>
          ))}
        </div>

        {/* Main Charts Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Portfolio Performance - Takes 2 columns */}
          <Card className="lg:col-span-2 overflow-hidden">
            <h2 className="text-xl font-bold mb-6 bg-gradient-to-r from-[#22c55e] to-[#16a34a] bg-clip-text text-transparent">
              Portfolio Performance
            </h2>
            {portfolioData.length === 0 ? (
              <div className="h-64 flex items-center justify-center">
                <p className="text-[var(--text-primary)] opacity-60">No P&amp;L records in this range.</p>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={portfolioData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1F2937" />
                  <XAxis dataKey="period" stroke="#A1A1AA" />
                  <YAxis stroke="#A1A1AA" tickFormatter={formatCurrency} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#161A1D",
                      border: "1px solid #22c55e",
                      borderRadius: "12px",
                      color: "#22c55e",
                    }}
                    formatter={(value) => [formatCurrency(value), "Cumulative P&L"]}
                  />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="#22c55e"
                    strokeWidth={3}
                    dot={{ fill: "#22c55e", r: 3 }}
                    activeDot={{ r: 5 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </Card>

          {/* Daily Trading Volume */}
          <Card className="overflow-hidden">
            <h2 className="text-xl font-bold mb-6 bg-gradient-to-r from-[#22c55e] to-[#16a34a] bg-clip-text text-transparent">
              Daily Trading Volume
            </h2>
            {volumeData.length === 0 ? (
              <div className="h-64 flex items-center justify-center">
                <p className="text-[var(--text-primary)] opacity-60">No trade volume in this range.</p>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <AreaChart data={volumeData}>
                  <defs>
                    <linearGradient id="volumeGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#22c55e" stopOpacity={0.4} />
                      <stop offset="100%" stopColor="#22c55e" stopOpacity={0.05} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1F2937" />
                  <XAxis dataKey="period" stroke="#A1A1AA" />
                  <YAxis stroke="#A1A1AA" tickFormatter={formatCurrency} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#161A1D",
                      border: "1px solid #22c55e",
                      borderRadius: "12px",
                      color: "#22c55e",
                    }}
                    formatter={(value) => [formatCurrency(value), "Volume"]}
                  />
                  <Area
                    dataKey="volume"
                    stroke="#22c55e"
                    fillOpacity={1}
                    fill="url(#volumeGradient)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </Card>
        </div>

        {/* Bottom Row - Asset Distribution & Strategy Effectiveness */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Asset Class Distribution */}
          <Card className="overflow-hidden">
            <h2 className="text-xl font-bold mb-6 bg-gradient-to-r from-[#22c55e] to-[#16a34a] bg-clip-text text-transparent">
              Income Source Distribution
            </h2>
            {incomeDistribution.length === 0 ? (
              <div className="h-64 flex items-center justify-center">
                <p className="text-[var(--text-primary)] opacity-60">No income distribution data available.</p>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={incomeDistribution}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    innerRadius={55}
                  >
                    {incomeDistribution.map((item, index) => (
                      <Cell key={item.name} fill={item.color || ["#22c55e", "#16a34a"][index % 2]} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#161A1D",
                      border: "1px solid #22c55e",
                      borderRadius: "12px",
                      color: "#22c55e",
                    }}
                    formatter={(value) => [`${value}%`, "Share"]}
                  />
                  <Legend wrapperStyle={{ color: "#22c55e" }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </Card>

          {/* Strategy Effectiveness */}
          <Card className="overflow-hidden">
            <h2 className="text-xl font-bold mb-6 bg-gradient-to-r from-[#22c55e] to-[#16a34a] bg-clip-text text-transparent">
              Trade Outcome Breakdown
            </h2>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={outcomeBreakdown}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1F2937" />
                <XAxis dataKey="name" stroke="#A1A1AA" />
                <YAxis stroke="#A1A1AA" domain={[0, 100]} tickFormatter={formatPercent} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#161A1D",
                    border: "1px solid #22c55e",
                    borderRadius: "12px",
                    color: "#22c55e",
                  }}
                  formatter={(value) => [`${value}%`, "Rate"]}
                />
                <Bar dataKey="value" fill="#22c55e" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default TradingPerformance;
